import React from 'react'
import styled from 'styled-components'
import { TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import NetworkWhitepaper from './assets/NetworkWhitepaper'
import Governance from './assets/Governance'
import MonoplasmaAudit from './assets/MonoplasmaAudit'
import TokenSale from './assets/TokenSale'
import Whitepaper from './assets/Whitepaper'
import LimeChainAudit from './assets/LimeChainAudit'
import IsentropyAudit from './assets/IsentropyAudit'
import Layout from '~shared/Layout'
import useTranslation from '~utils/useTranslation'
import urls from '~utils/urls'
import Link from '~shared/Link'
import Spacer from '~shared/Spacer'
import { Headline, Paragraph } from '~/components/DataFund/Hero'

const PapersLayout = styled(Layout)`
    ${Layout.Inner} {
        background-color: #ffffff;
        color: #0C009A;
        padding-top: 64px;
    }

    @media (min-width: ${DESKTOP}px) {
        ${Layout.Inner} {
            padding-top: 72px;
        }
    }
`

const Hero = styled.div`
    background-color: #ffffff;
    margin: 0 auto;
    padding: 5.5rem 2rem;

    @media (min-width: ${TABLET}px) {
        width: 580px;
    }

    @media (min-width: ${DESKTOP}px) {
        width: 580px;
    }

    p {
        font-size: 16px;
        line-height: 28px;
        margin: 0;
    }

    @media (min-width: ${DESKTOP}px) {
        p {
            font-size: 18px;
            line-height: 30px;
        }
    }
`

const PaperContainer = styled.div`
    background-color: #efefef;
    padding: 5.5rem 2rem;

    @media (min-width: ${TABLET}px) {
        padding: 12rem 7.5rem;
    }

    @media (min-width: ${DESKTOP}px) {
        padding: 11rem 13.5rem;
    }
`

const PaperList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, 144px);
    grid-column-gap: 24px;
    grid-row-gap: 65px;
    justify-content: center;

    @media (min-width: ${TABLET}px) {
        grid-template-columns: repeat(auto-fit, 224px);
        grid-column-gap: 80px;
        grid-row-gap: 65px;
    }

    @media (min-width: ${DESKTOP}px) {
        grid-template-columns: repeat(auto-fit, 224px);
        grid-column-gap: 76px;
    }
`

const PaperImage = styled.div`
    border-radius: 3px;
    display: block;
    overflow: hidden;
    transition: 300ms transform ease-out;
    width: 100%;
`

const Paper = styled(Link)`
    text-decoration: none !important;

    :hover ${PaperImage} {
        transform: scale(1.05);
        transition-duration: 50ms;
    }
`

const Name = styled.div`
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    margin-top: 16px;

    @media (min-width: ${TABLET}px) {
        font-size: 18px;
        line-height: 24px;
        margin-top: 30px;
    }

    @media (min-width: ${DESKTOP}px) {
        font-size: 18px;
        line-height: 24px;
        margin-top: 36px;
    }
`

const Date = styled.div`
    color: #a3a3a3;
    font-size: 12px;
    line-height: 20px;

    @media (min-width: ${TABLET}px) {
        font-size: 18px;
        line-height: 24px;
    }

    @media (min-width: ${DESKTOP}px) {
        font-size: 14px;
        line-height: 24px;
    }
`

const Papers = () => {
    const { t } = useTranslation()

    return (
        <PapersLayout>
            <Hero>
                <Spacer tail="32px,,">
                    <Headline as="h1" $appear>
                        {t('project.papers.title')}
                    </Headline>
                </Spacer>
                <Paragraph $appear>
                    {t('project.papers.description')}
                </Paragraph>
            </Hero>
            <PaperContainer>
                <PaperList>
                    <Paper href={urls.documents.limeChainAudit} newTab $appear>
                        <PaperImage as={LimeChainAudit} />
                        <div $appear>
                            <Name>{t('project.papers.limeChainAudit.title')}</Name>
                            <Date>{t('project.papers.limeChainAudit.date')}</Date>
                        </div>
                    </Paper>
                    <Paper href={urls.documents.isentropyAudit} newTab $appear>
                        <PaperImage as={IsentropyAudit} />
                        <div $appear>
                            <Name>{t('project.papers.isentropyAudit.title')}</Name>
                            <Date>{t('project.papers.isentropyAudit.date')}</Date>
                        </div>
                    </Paper>
                    <Paper href={urls.documents.networkWhitepaper} newTab $appear>
                        <PaperImage as={NetworkWhitepaper} />
                        <div $appear>
                            <Name>{t('project.papers.networkWhitepaper.title')}</Name>
                            <Date>{t('project.papers.networkWhitepaper.date')}</Date>
                        </div>
                    </Paper>
                    <Paper href={urls.documents.governanceWhitepaper} newTab $appear>
                        <PaperImage as={Governance} />
                        <div $appear>
                            <Name>{t('project.papers.governanceWhitepaper.title')}</Name>
                            <Date>{t('project.papers.governanceWhitepaper.date')}</Date>
                        </div>
                    </Paper>
                    <Paper href={urls.documents.monoplasmaAudit} newTab $appear>
                        <PaperImage as={MonoplasmaAudit} />
                        <div $appear>
                            <Name>{t('project.papers.monoplasmaAudit.title')}</Name>
                            <Date>{t('project.papers.monoplasmaAudit.date')}</Date>
                        </div>
                    </Paper>
                    <Paper href={urls.documents.tokenAudit} newTab $appear>
                        <PaperImage as={TokenSale} />
                        <div $appear>
                            <Name>{t('project.papers.tokenAudit.title')}</Name>
                            <Date>{t('project.papers.tokenAudit.date')}</Date>
                        </div>
                    </Paper>
                    <Paper href={urls.documents.whitepaper} newTab $appear>
                        <PaperImage as={Whitepaper} />
                        <div $appear>
                            <Name>{t('project.papers.whitepaper.title')}</Name>
                            <Date>{t('project.papers.whitepaper.date')}</Date>
                        </div>
                    </Paper>
                </PaperList>
            </PaperContainer>
        </PapersLayout>
    )
}

export default Papers
